// The one writer for a gate-held button's disabled reason (onboarding wizard,
// #13369 — A11Y-1 / BUTTON-4). Every step's gate controller decides WHETHER its
// button is ready and WHY not; this decides HOW the button says so:
//
//   * closed gate — `disabled`, `aria-disabled="true"`, the reason as the
//     button's own `data-gl-tooltip` (the global CSS-only tooltip primitive,
//     which restores pointer events on :disabled hosts) and an accessible name
//     of "<label> — <reason>" — the FormModal / gl_disabled_reason_attrs shape
//     (#13003), so a screen reader hears what the button does AND why it is
//     unavailable;
//   * open gate — enabled, and the button's OWN name and tooltip are restored
//     (#15282), so an enabled button never keeps telling the user to do
//     something they already did, and never loses its name.
//
// The button's own pair comes from one of two places:
//
//   * a text button (every onboarding Continue, Add Tag, Submit W-9, …) has
//     no authored aria-label or tooltip — its name IS its text. Its own pair
//     is "nothing", so the open state withdraws both attributes and the
//     visible text names it again (the behaviour these consumers always had);
//   * an icon-only button (a gl_row_action_button, e.g. Bucket Maint's Run)
//     has no text to fall back on, so it declares its own pair as
//     `data-gl-gate-label` / `data-gl-gate-tooltip` (stamped by
//     GlButtonsHelper#gl_row_action_button whenever it renders a
//     `disabled_reason:`). A button that reaches this module ENABLED with an
//     authored aria-label is recorded into the same two attributes on the
//     first call, so a gate that closes it later can still restore it.
//
// A native `title` is never written and any one found is removed: it is the
// banned mechanism (DESIGN.md BUTTON-4) the gates used before this module and
// it would stack a second bubble on the tooltip primitive.
//
// The composed name is rebuilt from the own label (or the visible label) on
// every call rather than from the previous aria-label, so a changing reason
// never compounds.
export const NAME_SEPARATOR = " — "

export function syncGateReason(button, { ready, reason }) {
  // A button whose request is in flight (aria-busy, the #14560 busy state)
  // is never re-OPENED by a gate: an edit typed mid-request must not enable
  // it and send a second request. Its owner clears aria-busy, then re-gates
  // it, when the request settles. Closing a busy button (with its reason,
  // e.g. the toast's busy action) goes through as usual.
  if (ready && button.getAttribute("aria-busy") === "true") {
    button.disabled = true
    return
  }
  recordOwnName(button)
  button.disabled = !ready
  button.setAttribute("aria-disabled", String(!ready))
  button.removeAttribute("title")

  if (ready) {
    restoreOwnName(button)
    return
  }

  button.setAttribute("data-gl-tooltip", reason)
  const label = ownLabel(button) || visibleLabel(button)
  button.setAttribute("aria-label", label ? `${label}${NAME_SEPARATOR}${reason}` : reason)
}

// An enabled button with an authored name, first seen here, keeps that name:
// record it once so the open state can put it back. A disabled button's
// aria-label may already be a composed "<label> — <reason>", so it is never
// read as the own name — that is what data-gl-gate-label is for.
function recordOwnName(button) {
  if (button.hasAttribute("data-gl-gate-label") || button.disabled) return
  const authored = button.getAttribute("aria-label")
  if (!authored) return

  button.setAttribute("data-gl-gate-label", authored)
  const tooltip = button.getAttribute("data-gl-tooltip")
  if (tooltip) button.setAttribute("data-gl-gate-tooltip", tooltip)
}

function restoreOwnName(button) {
  const label = ownLabel(button)
  if (label) {
    button.setAttribute("aria-label", label)
  } else {
    button.removeAttribute("aria-label")
  }

  const tooltip = button.getAttribute("data-gl-gate-tooltip")
  if (tooltip) {
    button.setAttribute("data-gl-tooltip", tooltip)
  } else {
    button.removeAttribute("data-gl-tooltip")
  }
}

function ownLabel(button) {
  return (button.getAttribute("data-gl-gate-label") || "").trim()
}

// The visible label: an <input type=submit> shows its value, a <button> its
// text (a decorative <i aria-hidden> icon contributes none).
function visibleLabel(button) {
  const raw = button instanceof HTMLInputElement ? button.value : button.textContent
  return (raw || "").replace(/\s+/g, " ").trim()
}
